import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { ZINDEX_HIGH_2 } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { formatAmountWithCurrency } from 'utils/generic/currency-format';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { showToastSuccess } from 'utils/swarm/swarm-success-handler';
import { ICasinoCachedGames } from 'interfaces/casino-data';
import { Modal } from 'components/Modal';
import { Button } from 'newcomponents/UI/Button';
import { getActiveGames } from 'store/selectors/new-casino';
import { getUser } from 'store/selectors/user-data';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

export const NewDepositPopup = () => {
  const { t } = useTranslation();
  const user = useSelector(getUser);
  const [depositPopupVisible, setDepositPopupVisible] = useState(false);
  const [paymentData, setPaymentData] = useState<{ amount: string } | null>(
    null
  );

  const activeGames = useSelector(getActiveGames) as ICasinoCachedGames;
  const isThereAnyActiveGame = Object.keys(activeGames).length > 0;
  const location = window.location.search;

  useEffect(() => {
    const shouldDisplay = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'DEPOSIT_AMOUNT'))
    );

    const paymentDataStorage = JSON.parse(
      LocalStorage.getItem(storageKeyName('account', 'PAYMENT_DATA'))
    );

    setPaymentData(paymentDataStorage);

    setDepositPopupVisible(shouldDisplay);
  }, []);

  useEffect(() => {
    if (
      !location.includes('message=') ||
      !isThereAnyActiveGame ||
      !user.currency
    ) {
      return;
    }

    showToastSuccess(
      `${paymentData?.amount} ${user.currency} ${t(
        'account.successAddedToYourBallanceMessage'
      )} `
    );
    LocalStorage.removeItem(storageKeyName('account', 'DEPOSIT_AMOUNT'));
    LocalStorage.removeItem(storageKeyName('account', 'PAYMENT_DATA'));
    setDepositPopupVisible(false);
  }, [isThereAnyActiveGame, user]);

  if (!location.includes('accounts=') || !paymentData) {
    return null;
  }

  return (
    <Modal
      zIndex={ZINDEX_HIGH_2}
      align="center"
      centered
      title={t('account.successDepositMessage')}
      visible={depositPopupVisible && !!user.currency}
      className="paymentSuccessModal"
      bodyStyle={{ padding: isMobile() ? '12px' : 0 }}
      onCancel={() => {
        LocalStorage.removeItem(storageKeyName('account', 'DEPOSIT_AMOUNT'));
        setDepositPopupVisible(false);

        document.body.classList.remove('no-scroll');
      }}
      image={{ lib: 'generic', name: 'success', theme: 'colored' }}
      renderInBody
    >
      <span
        className="paymentSuccessModal--message"
        dangerouslySetInnerHTML={{
          __html: `<span dir='ltr'>${formatAmountWithCurrency(
            user.currency,
            paymentData?.amount
          )}</span>  ${t('account.successAddedToYourBallanceMessage')}`
        }}
      />

      <div className="paymentSuccessModal--button">
        <Button
          size="large"
          type="primary"
          block
          onClick={() => {
            document.body.classList.remove('no-scroll');
            setDepositPopupVisible(false);
            LocalStorage.removeItem(
              storageKeyName('account', 'DEPOSIT_AMOUNT')
            );
            LocalStorage.removeItem(storageKeyName('account', 'PAYMENT_DATA'));
          }}
        >
          {t('account.ok')}
        </Button>
      </div>
    </Modal>
  );
};
